import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { format } from 'timeago.js';
import './RespondToSuggestion.css';

const API = process.env.REACT_APP_API_BASE_URL;
// Simple polling, not a live socket connection — this is a minimal,
// scoped-access landing page for a 24h chat link (see
// conversationAccessToken.js), not the full authenticated Messenger UI,
// so it deliberately doesn't take on socket auth/identity complexity.
const POLL_MS = 4000;

// The public landing page for a Matched Intro email's chat link — visually
// mirrors the real in-app Messenger (chat header, message bubbles,
// per-message avatars, "X left the conversation" system notice, a
// profile-card sidebar) so it doesn't feel like a stripped-down imitation,
// but is otherwise a self-contained minimal chat view for exactly the one
// conversation the token is scoped to: no sidebar of other conversations,
// no login, no access to anything else in the app. It talks only to
// backend/routes/messengerIntro.js's two scoped endpoints — deliberately
// NOT the authenticated MessengerContext the real Messenger.js page uses,
// so this page's blast radius stays limited to one conversation even
// though it now looks like the real thing.
const MessengerIntro = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [context, setContext] = useState(null);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  // Which of the (at most two) conversations this token is allowed to
  // reach is currently being viewed — null means the primary one the
  // link was actually sent for (default); set to the community-bot
  // conversation's id when the user picks it from the sidebar. See
  // backend/routes/messengerIntro.js's resolveAllowedConversations for
  // why only these two, ever.
  const [viewConversationId, setViewConversationId] = useState(null);
  // Single-panel mobile mode (<1024px, matching the real Messenger's own
  // breakpoint) — which of the three columns is currently shown, same
  // back-arrow/tap-through pattern as the real app's
  // onBackToChatMenuForCSS/onToChatProfileForCSS. Ignored at wider
  // widths, where all three columns always show together (see CSS).
  const [mobileView, setMobileView] = useState('chat'); // 'gate' | 'chat' | 'profile'
  // Real Messenger's AI assistant (find-others-to-join / schedule) hits
  // authenticated-only endpoints (see MessengerInput.js's
  // handleConfirmFindMembers — Firebase ID token, /group-match/invite-bot)
  // — not something a scoped, no-login token can safely trigger. Shown
  // for visual parity, but both options gate to a login prompt instead
  // of actually running, same pattern as the sidebar's own login note.
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  const [aiModal, setAiModal] = useState(null); // 'find' | 'schedule' | null
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const fetchContext = async (isInitial, viewId) => {
    if (!token) return;
    try {
      const viewParam = viewId ? `&viewConversationId=${encodeURIComponent(viewId)}` : '';
      const res = await fetch(`${API}/messenger-intro/context?token=${encodeURIComponent(token)}${viewParam}`);
      const data = await res.json();
      if (!res.ok) {
        if (isInitial) setError(data.error || 'This link has expired.');
        return;
      }
      setContext(data);
    } catch (err) {
      if (isInitial) setError('Something went wrong loading this conversation — please try again shortly.');
    }
  };

  useEffect(() => {
    if (!token) { setError('This link looks incomplete — please use the link from your email directly.'); return; }
    fetchContext(true, viewConversationId);
    pollRef.current = setInterval(() => fetchContext(false, viewConversationId), POLL_MS);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, viewConversationId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [context?.messages?.length]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!draft.trim() || sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/messenger-intro/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, text: draft, conversationId: viewConversationId || undefined }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Could not send your message.'); return; }
      setDraft('');
      fetchContext(false, viewConversationId);
    } catch (err) {
      setError('Could not send your message — please try again.');
    } finally {
      setSending(false);
    }
  };

  const selectConversation = (conversationId) => {
    setViewConversationId((prev) => {
      const isPrimary = context?.sidebar?.[0]?.conversationId === conversationId;
      return isPrimary ? null : conversationId;
    });
    setContext(null);
    setMobileView('chat');
  };

  const memberFor = (senderId) => context?.members.find((m) => m.userId === senderId);
  const nameFor = (senderId) => {
    if (context && senderId === context.userId) return 'You';
    return memberFor(senderId)?.firstName || 'Them';
  };

  if (error) {
    return (
      <div className="respond-page">
        <div className="respond-page-inner">
          <div className="respond-error">{error}</div>
          <div className="respond-intro-sub" style={{ marginTop: '1rem' }}>
            This link works for 24 hours from when it was sent. After that, please <Link to="/login">log in</Link> to keep chatting.
          </div>
        </div>
      </div>
    );
  }

  if (!context) {
    return (
      <div className="respond-loading">
        <div className="respond-spinner" />
      </div>
    );
  }

  const others = context.members.filter((m) => m.userId !== context.userId);
  const otherNames = others.map((m) => m.firstName || 'them');
  const otherNamesText = otherNames.length <= 1
    ? (otherNames[0] || 'them')
    : `${otherNames.slice(0, -1).join(', ')} and ${otherNames[otherNames.length - 1]}`;
  const headerTitle = context.isBotActive ? `${context.communityName} AI` : `Chat with ${otherNamesText}`;

  return (
    <div className="respond-page">
      <div className="intro-chat-page-wrap">
        <div className={`intro-chat-shell intro-chat-shell--mobile-${mobileView}`}>
          {/* Real conversation list, but only ever the (at most) two
              conversations this token can reach — the matched-intro
              conversation itself, and this user's community-bot
              conversation for the same community. Anything beyond that
              needs a real login, noted below the list. */}
          <div className="intro-chat-gate">
            <div className="intro-chat-gate-heading">Messages</div>
            {context.sidebar.map((row) => (
              <button
                key={row.conversationId}
                type="button"
                className={`intro-chat-gate-row ${row.conversationId === context.activeConversationId ? 'intro-chat-gate-row--active' : ''}`}
                onClick={() => selectConversation(row.conversationId)}
              >
                {row.photo
                  ? <img src={row.photo} alt="" className="intro-chat-gate-row-avatar" />
                  : <div className="intro-chat-gate-row-avatar intro-chat-gate-row-avatar--placeholder">?</div>}
                <div className="intro-chat-gate-row-text">
                  <div className="intro-chat-gate-row-name">{row.name}</div>
                  <div className="intro-chat-gate-row-preview">{row.preview}</div>
                </div>
              </button>
            ))}
            <div className="intro-chat-gate-note">
              This link only opens these conversations. <Link to="/login" className="intro-chat-gate-login">Log in</Link> to see everything else.
            </div>
          </div>

          <div className="intro-chat-main">
            <div className="intro-chat-header">
              <button type="button" className="intro-chat-header-back" onClick={() => setMobileView('gate')} aria-label="Back to messages">←</button>
              <div className="intro-chat-header-clickable" onClick={() => setMobileView('profile')}>
                <div className="intro-chat-header-avatars">
                  {context.isBotActive ? (
                    context.communityPhoto
                      ? <img src={context.communityPhoto} alt="" className="intro-chat-header-avatar" />
                      : <div className="intro-chat-header-avatar intro-chat-header-avatar--placeholder">?</div>
                  ) : others.map((m, i) => (
                    m.photo
                      ? <img key={m.userId} src={m.photo} alt="" className="intro-chat-header-avatar" style={{ zIndex: others.length - i }} />
                      : <div key={m.userId} className="intro-chat-header-avatar intro-chat-header-avatar--placeholder" style={{ zIndex: others.length - i }}>?</div>
                  ))}
                </div>
                <div className="intro-chat-header-title-block">
                  <div className="intro-chat-header-title">{headerTitle}</div>
                  {/* Not for the bot conversation itself — "member of X"
                      would just restate "X AI" right above it. */}
                  {!context.isBotActive && context.communityName && (
                    <div className="intro-chat-header-subtitle">Member of {context.communityName}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="intro-chat-messages">
              {context.messages.length === 0 && (
                <div className="as-card-meta">No messages yet — say hello to {otherNamesText}!</div>
              )}
              {context.messages.map((m) => {
                const isMine = m.sender === context.userId;
                const sender = memberFor(m.sender);
                // The community itself (not a real member) sends two
                // kinds of message this page ever shows: the primary
                // conversation's one intro message (flagged
                // leaveConvoAfter), and every message in the bot
                // conversation when it's the active one (context.members
                // deliberately excludes the community's own id there, so
                // memberFor(m.sender) is always undefined for its
                // messages — same avatar source the real Messenger uses
                // for this case, see NewMessages.js's getOtherUserPic:
                // community?.pics when sender === communityId).
                const isCommunitySender = m.leaveConvoAfter || (context.isBotActive && !isMine);
                const avatarPhoto = isCommunitySender ? context.communityPhoto : sender?.photo;
                return (
                  <React.Fragment key={m._id}>
                    <div className={`intro-chat-message ${isMine ? 'intro-chat-message--own' : ''}`}>
                      <div className="intro-chat-message-top">
                        {avatarPhoto
                          ? <img src={avatarPhoto} alt="" className="intro-chat-message-avatar" />
                          : <div className="intro-chat-message-avatar intro-chat-message-avatar--placeholder">{isMine ? nameFor(m.sender).charAt(0) : '?'}</div>}
                        <div className="intro-chat-bubble">{m.text}</div>
                      </div>
                      <div className="intro-chat-message-time">{format(m.createdAt)}</div>
                    </div>
                    {m.leaveConvoAfter && (
                      <div className="intro-chat-system-notice">
                        {m.communityName || context.communityName} left the conversation
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSend} className="intro-chat-input-bar">
              <div className="intro-chat-ai-area">
                <button
                  type="button"
                  className={`intro-chat-ai-btn${aiMenuOpen ? ' intro-chat-ai-btn--open' : ''}`}
                  onClick={() => setAiMenuOpen((v) => !v)}
                  title="AI assistant options"
                >
                  ✨ AI
                </button>
                {aiMenuOpen && (
                  <div className="intro-chat-ai-dropdown">
                    <button type="button" className="intro-chat-ai-option" onClick={() => { setAiMenuOpen(false); setAiModal('find'); }}>
                      <span>👥</span> Find others to join the chat
                    </button>
                    <button type="button" className="intro-chat-ai-option" onClick={() => { setAiMenuOpen(false); setAiModal('schedule'); }}>
                      <span>📅</span> Schedule a time / place
                    </button>
                  </div>
                )}
              </div>
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Type a message…"
                disabled={sending}
              />
              <button type="submit" className="intro-chat-send-btn" disabled={sending || !draft.trim()} aria-label="Send">➤</button>
            </form>
          </div>

          {aiModal && (
            <div className="intro-chat-ai-modal-overlay" onClick={() => setAiModal(null)}>
              <div className="intro-chat-ai-modal" onClick={(e) => e.stopPropagation()}>
                <h3 className="intro-chat-ai-modal-title">
                  {aiModal === 'find' ? 'Find other people to join the chat' : 'Schedule a time / place'}
                </h3>
                <p className="intro-chat-ai-modal-body">
                  {aiModal === 'find'
                    ? "AI can scan your community for someone who looks like a great match and invite them to join this chat — but that's a full-account feature. Log in to use it."
                    : 'AI can help everyone here agree on a time and place to meet — but that\'s a full-account feature. Log in to use it.'}
                </p>
                <div className="intro-chat-ai-modal-actions">
                  <Link to="/login" className="intro-chat-ai-modal-confirm">Log in</Link>
                  <button type="button" className="intro-chat-ai-modal-cancel" onClick={() => setAiModal(null)}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          <div className="intro-chat-profile-panel">
            <button type="button" className="intro-chat-profile-back" onClick={() => setMobileView('chat')} aria-label="Back to chat">← Back to chat</button>
            {context.isBotActive ? (
              <div className="intro-chat-profile-details">
                <div className="intro-chat-profile-name">{context.communityName}</div>
                <p className="intro-chat-profile-bio-text">This is your community's own chat — not an individual profile.</p>
              </div>
            ) : others.map((m) => (
              <div key={m.userId} className="intro-chat-profile-card">
                {m.photo
                  ? <img src={m.photo} alt="" className="intro-chat-profile-photo" />
                  : <div className="intro-chat-profile-photo--placeholder">?</div>}
                <div className="intro-chat-profile-details">
                  <div className="intro-chat-profile-name">{m.firstName}{m.age ? `, ${m.age}` : ''}</div>
                  {m.bio && (
                    <>
                      <div className="intro-chat-profile-bio-label">{m.firstName}'s {context.communityName} bio:</div>
                      <p className="intro-chat-profile-bio-text">{m.bio}</p>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessengerIntro;
